import type { AnimationEvent, CSSProperties } from 'react'

type Props = {
  active: boolean
  label: string
  onClick: () => void
  durationMs: number
  showLoader: boolean
  loaderCycle: number
  isTransitioning: boolean
  isPlaying: boolean
  onLoaderComplete?: () => void
}

const DOT_SIZE = 29
const RING_R = 11.5
const CENTER_R = 5.5
const CX = 14.5
const CY = 14.5
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

/** Active slide progress ring + center dot (multifamily hero slider). */
export default function HeroSliderDot({
  active,
  label,
  onClick,
  durationMs,
  showLoader,
  loaderCycle,
  isTransitioning,
  isPlaying,
  onLoaderComplete,
}: Props) {
  const showRing = showLoader && active && !isTransitioning && loaderCycle > 0

  const handleAnimationEnd = (event: AnimationEvent<SVGCircleElement>) => {
    if (event.animationName !== 'hero-slider-dot-fill') return
    if (active && isPlaying && showLoader) {
      onLoaderComplete?.()
    }
  }

  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      aria-label={label}
      onClick={onClick}
      className="flex size-[29px] shrink-0 cursor-pointer items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
    >
      <svg
        className="size-[29px]"
        viewBox={`0 0 ${DOT_SIZE} ${DOT_SIZE}`}
        fill="none"
        aria-hidden="true"
      >
        {active ? (
          <>
            {showLoader && (
              <circle
                key={loaderCycle}
                cx={CX}
                cy={CY}
                r={RING_R}
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                transform={`rotate(-90 ${CX} ${CY})`}
                className={showRing ? 'hero-slider-dot-ring' : undefined}
                onAnimationEnd={handleAnimationEnd}
                style={
                  {
                    '--loader-duration': `${durationMs}ms`,
                    '--ring-circumference': `${RING_CIRCUMFERENCE}`,
                    strokeDasharray: `${RING_CIRCUMFERENCE}`,
                    strokeDashoffset: `${RING_CIRCUMFERENCE}`,
                    animationPlayState: isPlaying ? 'running' : 'paused',
                  } as CSSProperties
                }
              />
            )}
            <circle cx={CX} cy={CY} r={CENTER_R} fill="white" />
          </>
        ) : (
          <circle
            cx={CX}
            cy={CY}
            r={CENTER_R}
            fill="white"
            opacity={showLoader ? undefined : 0.5}
          />
        )}
      </svg>
    </button>
  )
}
